import { ServiceDetails } from '@/components/service-details'
import { IAppointment, IServiceHours } from '@/service/types'
import { getExpiresAt } from '@/utils'
import { Box, Paper, Theme, Typography, useMediaQuery, useTheme } from '@mui/material'
import { JSX } from 'react'

export interface ExpirationTimeProps {
  clientId: string
  hours: IServiceHours[]
  theme: Theme
}

const ExpirationTime = (props: ExpirationTimeProps) => {
  const { clientId, hours, theme } = props

  let myAppointment = ''
  hours.forEach(({ appointments }) => {
    appointments.forEach((appointment) => {
      if (appointment.isLocked && appointment.visitor === clientId) {
        myAppointment = appointment.expiresAt
      }
    })
  })

  if (!myAppointment) {
    return <></>
  }

  const expiresAt = getExpiresAt(myAppointment)

  return (
    <Typography
      sx={{
        color:
          expiresAt.startsWith('(0:2') ||
          expiresAt.startsWith('(0:1') ||
          expiresAt.startsWith('(0:0')
            ? theme.palette.error.main
            : theme.palette.success.main,
      }}
      variant="body1"
    >
      {expiresAt}
    </Typography>
  )
}

export interface IServiceAppointmentsProps {
  renderAppointment: (appointment: IAppointment) => JSX.Element | false
  clientId: string
  service: string
  hours: IServiceHours[]
  showBookedAppointments: boolean
}

export const ServiceAppointments = (props: IServiceAppointmentsProps) => {
  const { service, hours, renderAppointment, clientId, showBookedAppointments } = props

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  if (isDesktop) {
    return (
      <Paper elevation={1} sx={styles.container}>
        <Box sx={{ minWidth: 200 }}>
          <ServiceDetails service={service}>
            <ExpirationTime clientId={clientId} hours={hours} theme={theme} />
          </ServiceDetails>
        </Box>

        <Box sx={styles.hoursBlock}>
          {hours.map(
            ({ hour, appointments }) =>
              appointments.filter((ap) => showBookedAppointments || ap.status === 'AVAILABLE')
                .length > 0 && (
                <Box key={service + hour} sx={styles.hourContainer}>
                  <Box>
                    <Typography variant="body1">{hour}:00</Typography>
                  </Box>

                  <Box sx={styles.appointmentsContainer}>
                    {appointments
                      .filter((ap) => (showBookedAppointments ? true : ap.status === 'AVAILABLE'))
                      .map((appointment) => renderAppointment(appointment))}
                  </Box>
                </Box>
              ),
          )}
        </Box>
      </Paper>
    )
  }

  return (
    <Paper elevation={1} sx={{ marginTop: 4, padding: 1 }}>
      <ServiceDetails service={service} mode="mobile">
        <ExpirationTime clientId={clientId} hours={hours} theme={theme} />
      </ServiceDetails>

      <Box>
        {hours.map(
          ({ hour, appointments }) =>
            appointments.filter((ap) => showBookedAppointments || ap.status === 'AVAILABLE')
              .length > 0 && (
              <Box key={service + hour}>
                <Box>
                  <Typography color="primary" variant="h6" sx={{ paddingTop: 2 }}>
                    {hour}:00
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    maxWidth: '100%',
                    overflow: 'scroll',
                    paddingY: 1,
                  }}
                >
                  {appointments
                    .filter((ap) => ap.status === 'AVAILABLE')
                    .map((appointment) => renderAppointment(appointment))}
                </Box>
              </Box>
            ),
        )}
      </Box>
    </Paper>
  )
}

const styles = {
  container: { display: 'flex', padding: 2, marginX: 1, marginY: 2 },
  hoursBlock: { display: 'flex', flexDirection: 'column', paddingLeft: 2 },
  hourContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 3,
    alignItems: 'baseline',
  },
  appointmentsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    paddingLeft: 2,
  },
}
